import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/contexts/AuthContext';
import { createApiClient } from '@/shared/api/apiClient';
import type { components } from '@/shared/api/openapi';

import type { BaseQuery } from './baseQuery.types';
import { getImportPollingInterval } from './importPolling';

export type ImportedAllocationFileHistoryItem =
  components['schemas']['GetImportedAllocationFileHistoryEndpoint_ImportedFileHistoryResponse'];

type GetImportedAllocationFileHistoryPagedResponse =
  components['schemas']['GetImportedAllocationFileHistoryEndpoint_ImportedFileHistoryPagedResponse'];

export interface UseImportedAllocationFileHistoryReturn extends BaseQuery {
  importHistory: GetImportedAllocationFileHistoryPagedResponse | null;
}

export function useImportedAllocationFileHistory(
  organizationId: number | undefined,
  trackedImportId?: number | null,
  page: number = 1,
  pageSize: number = 10,
  sortedBy: string | null = null,
): UseImportedAllocationFileHistoryReturn {
  const { getAccessToken } = useAuth();

  const getImportedAllocationFileHistory = async () => {
    if (!organizationId || organizationId === 0) {
      return null;
    }

    const client = createApiClient(getAccessToken);
    const { data } = await client.GET('/api/organizations/{organizationId}/allocation-groups/imported-file-history', {
      params: {
        path: { organizationId },
        query: {
          Page: page,
          PageSize: pageSize,
          ...(sortedBy ? { SortedBy: sortedBy } : {}),
        },
      },
    });

    if (!data) {
      throw new Error('No data returned from API');
    }

    return data as GetImportedAllocationFileHistoryPagedResponse;
  };

  const query = useQuery({
    queryKey: ['importedAllocationFileHistory', organizationId, page, pageSize, sortedBy],
    queryFn: async () => {
      try {
        return await getImportedAllocationFileHistory();
      } catch (err) {
        if (err instanceof Error) {
          throw err;
        }
        throw new Error(err ? String(err) : 'Unknown error occurred');
      }
    },
    enabled: !!organizationId && organizationId > 0,
    retry: false,
    refetchInterval: (query) => getImportPollingInterval(query.state.data?.items ?? undefined, trackedImportId),
  });

  const { data, isLoading, error, refetch, isRefetching } = query;

  return {
    importHistory: data ?? null,
    isLoading,
    isRefetching,
    error: error as Error | null,
    refetch,
  };
}
